import { ref } from 'vue';
import { defineStore } from 'pinia';
import { FilterMatchMode } from '@primevue/core/api';

export type PricingRow = {
  inv_id: number;
  inv_number: string;
  inv_status: string | null;
  inv_type: string | null;
  prices: Record<string, string | null>;
  floors: Record<string, string | null>;
};

export type PricingComponentOption = {
  key: string;
  label: string;
  hasFloor: boolean;
};

export type PricingComponentAdmin = {
  id: number;
  key: string;
  label: string;
  isEnabled: boolean;
  hasFloor: boolean;
  isLocked: boolean;
};

export type SelectOption = {
  code: string;
  name: string;
};

export type SalesContextCode = 'local' | 'national';

export const pricingDescriptions = {
  adSpaceFloor: 'The minimum sellable Ad Space rate. Once yield pricing calculates a proposed rate, this floor is applied as a final safety clamp — raising the rate up to the floor and tightening the maximum discount so no further discounting can sell below it. Enforcement only applies to occupied, non-Bonus contract classes. If yield provides a discount that would be more strict than the floor, that will still be enforced',
  automatedSales: 'Automated Sales will be the base rates that Red Dragon builds its automated pricing on.',
  // Ad Space Floor is automatically enforced by yield pricing (see adSpaceFloor above); no other
  // component's floor is wired into that enforcement yet, so its tooltip says so rather than
  // implying a safety clamp that doesn't exist for it.
  floorTooltip: (componentKey: string, componentLabel: string): string => (componentKey === 'ad_space'
    ? pricingDescriptions.adSpaceFloor
    : `The minimum sellable ${componentLabel} rate. Automatic yield-pricing enforcement is only wired up for Ad Space today, so setting a floor here does not yet clamp proposed rates for this component.`),
};

export type PricingTableFilters = Record<string, { value: string | string[] | null; matchMode: string }>;

// inv_number is user-filterable; inv_status/inv_type are multiselects. Per-component
// price columns (and per-component floor columns) are numeric searches; their filter
// entries are added dynamically once componentOptions/canViewFloor are known — see ManagePricing.vue.
const buildDefaultFilters = (): PricingTableFilters => Object.fromEntries([
  ['inv_number', { value: null, matchMode: FilterMatchMode.CONTAINS }],
  ['inv_status', { value: null, matchMode: FilterMatchMode.IN }],
  ['inv_type', { value: null, matchMode: FilterMatchMode.IN }],
]);

/**
 * Holds the Manage Pricing screen state so it survives the route component
 * being unmounted/remounted when the user navigates away and back, mirroring
 * useMeasurementsStore.
 */
export const usePricingStore = defineStore('pricing-store', () => {
  const rows = ref<PricingRow[]>([]);
  const totalRecords = ref(0);

  const componentOptions = ref<PricingComponentOption[]>([]);
  const contextOptions = ref<{ code: SalesContextCode; name: string }[]>([]);
  const venueOptions = ref<SelectOption[]>([]);
  const statusOptions = ref<SelectOption[]>([]);
  const canViewFloor = ref(false);

  // Admin Tools: whether the current user may manage pricing_components, and the
  // full component list (enabled + disabled) shown/edited there. originalPricingComponents
  // is a snapshot baseline refreshed on every successful load/save, used only to detect
  // unsaved edits in pricingComponents — see ManagePricing.vue's componentsDirty.
  const adminToolsEnabled = ref(false);
  const pricingComponents = ref<PricingComponentAdmin[]>([]);
  const originalPricingComponents = ref<PricingComponentAdmin[]>([]);

  const context = ref<SalesContextCode>('local');

  // Table view state.
  const rowsPerPage = ref(100);
  const firstRecord = ref(0);
  const tableSortField = ref<string | null>(null);
  const tableSortOrder = ref<number | null>(null);
  const tableFilters = ref<PricingTableFilters>(buildDefaultFilters());

  // False until the first load completes; gates the on-mount fetch.
  const initialized = ref(false);

  // Bulk-edit selection state. selectAllMatchingFilter means "every row matching the current
  // filter/context, resolved server-side" — deliberately never materialized as a client-side id
  // list, since the matched set can span thousands of rows across pages.
  const selectedRows = ref<PricingRow[]>([]);
  const selectAllMatchingFilter = ref(false);

  return {
    rows,
    totalRecords,
    componentOptions,
    contextOptions,
    venueOptions,
    statusOptions,
    canViewFloor,
    adminToolsEnabled,
    pricingComponents,
    originalPricingComponents,
    context,
    rowsPerPage,
    firstRecord,
    tableSortField,
    tableSortOrder,
    tableFilters,
    initialized,
    selectedRows,
    selectAllMatchingFilter,
  };
});
